import { useMutation } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors/transformError';
import { EXTENDED_GRAPHQL_TIMEOUT_MS } from '@core/services/constants';
import { useMemo } from 'react';

import type { GraphQLAuditDecision } from '../domain/graphql-types';
import { UPSERT_AUDIT_DECISION } from '../graphql/mutations/upsertAuditDecision';
import { updateAuditDecisionAndCaches } from '../utils/auditCacheUpdates';

interface UpsertAuditDecisionInput {
  // Required fields for simplified external QA workflow
  auditItemId: string;
  auditSessionId: string;
  originalLabels: Array<{
    name: string;
    severity: number;
  }>;
  labelsCorrect?: boolean | null;
  policiesCorrect?: boolean | null;

  // Optional fields for backward compatibility and advanced usage
  existingDecisionId?: string;
  shouldDelete?: boolean;
  languageCorrect?: boolean | null;
  transcriptionCorrect?: boolean | null;
  correctedTranscription?: string | null;
  ocrCorrect?: boolean | null;
  correctedOcr?: string | null;
  correctedLabels?: Array<{
    name: string;
    severity: number;
  }>;
  comments?: string | null;
  triggeredPolicyAssessments?: Array<{
    policyId: string;
    policyName: string;
    isCorrect: boolean;
    comments?: string;
  }> | null;
  nonTriggeredPolicyAssessments?: Array<{
    policyId: string;
    policyName: string;
    isCorrect: boolean;
    comments?: string;
  }> | null;
}

// GraphQL AuditItemWithDecisions type structure (minimal interface for this hook)
interface AuditItemWithDecisions {
  id: string;
  auditSessionId: string;
}

interface UseUpsertAuditDecisionOptions {
  item: AuditItemWithDecisions;
  existingDecision?: GraphQLAuditDecision;
}

/**
 * Hook for upserting audit decisions with automatic cache updates
 * The mutation now returns the complete audit item, eliminating the need for separate refresh
 */
export function useUpsertAuditDecision({ item: _item, existingDecision: _existingDecision }: UseUpsertAuditDecisionOptions) {
  const [upsertAuditDecisionMutation, { loading, error }] = useMutation(UPSERT_AUDIT_DECISION, {
    context: { timeout: EXTENDED_GRAPHQL_TIMEOUT_MS },
    update(cache, { data }) {
      if (data?.upsertAuditDecision?.auditItem) {
        // Use centralized cache update utility for consistent patterns
        updateAuditDecisionAndCaches(cache, data.upsertAuditDecision.auditItem);
      }
    },
  });

  // Transform errors using adapter pattern - return array for consistency
  const domainErrors = useMemo(() => {
    if (!error) return [];

    const mappedErrors = transformErrors(error);

    // Return error array for UI handling
    return mappedErrors;
  }, [error]);

  // Wrapper function that handles the mutation call with automatic cache update
  const upsertAuditDecision = async (input: UpsertAuditDecisionInput) => {
    try {
      const result = await upsertAuditDecisionMutation({
        variables: { input },
      });

      return {
        success: true,
        data: result.data?.upsertAuditDecision,
      };
    } catch (err) {
      return {
        success: false,
        error: err,
      };
    }
  };

  return {
    upsertAuditDecision,
    loading,
    errors: domainErrors,
  };
}
